import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getWarning, getSummary, acknowledgeWarning } from '../lib/db.js';
import { useAuth } from '../lib/auth.jsx';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';
import { thaiDate } from '../lib/format.js';

const LEVELS = [
  { n: 1, label: 'วาจา' },
  { n: 2, label: 'ลายลักษณ์' },
  { n: 3, label: 'ครั้งสุดท้าย' },
  { n: 4, label: 'เลิกจ้าง' },
];

export function WarningDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { employeeId } = useAuth();
  const [w, setW] = useState(null);
  const [empName, setEmpName] = useState('');
  const [err, setErr] = useState('');
  const [acking, setAcking] = useState(false);
  const [refuseMode, setRefuseMode] = useState(false);
  const [witness, setWitness] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const warn = await getWarning(id);
        if (!active) return;
        setW(warn);
        if (warn?.employee_id) {
          const s = await getSummary(warn.employee_id);
          if (active) setEmpName(s ? (s.nickname || `${s.first_name} ${s.last_name}`) : '');
        }
      } catch (e) {
        if (active) setErr(e.message);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const isMine = w && employeeId && w.employee_id === employeeId;
  const canAck = isMine && w.ack_status === 'pending';

  async function doAck(refuse) {
    if (refuse && !witness.trim()) { setErr('การปฏิเสธต้องระบุชื่อพยาน'); return; }
    setErr('');
    setAcking(true);
    try {
      const updated = await acknowledgeWarning(id, { refuse, witness: witness.trim() });
      setW((prev) => ({ ...prev, ...updated }));
      setRefuseMode(false);
    } catch (e) {
      setErr(e.message);
    }
    setAcking(false);
  }

  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <button className="back" onClick={() => nav(-1)}>‹ กลับ</button>
        {err && <p className="err">{err}</p>}
        {!w && !err && <div className="center"><div className="spinner" /></div>}
        {w === null && !err && null}

        {w && (
          <>
            <div className="title-row">
              <div>
                <div className="eyebrow">หนังสือตักเตือน{empName ? ` · ${empName}` : ''}</div>
                <h1 className="page">ขั้นที่ {w.ladder_level}</h1>
              </div>
              <span className="glyph">⚠︎</span>
            </div>

            <div className="hero">
              <div className="num small">{w.ladder_level}</div>
              <div className="sub">{levelTh(w.level_name)} · มีผลถึง {thaiDate(w.expires_at)}</div>
            </div>

            <div className="ladder">
              {LEVELS.map((l) => (
                <div key={l.n} className={`step${l.n <= w.ladder_level ? ' on' : ''}${l.n === w.ladder_level ? ' cur' : ''}`}>
                  <span className="n">{l.n}</span>{l.label}
                </div>
              ))}
            </div>
            <p className="note" style={{ marginTop: 10 }}>
              ใบเตือนมีอายุ 1 ปีนับจากวันเกิดเหตุ หากเกิดซ้ำในข้อเดียวกันจะเลื่อนขั้นถัดไป
            </p>

            <div className="sec-label">รายละเอียด</div>
            <div className="def"><span className="dk">ข้อบังคับ</span><span className="dv">{w.policy_clause || '—'}</span></div>
            <div className="def"><span className="dk">วันที่เกิดเหตุ</span><span className="dv">{thaiDate(w.incident_date)}</span></div>
            <div className="def"><span className="dk">หมดอายุ</span><span className="dv">{thaiDate(w.expires_at)}</span></div>
            <div className="def"><span className="dk">ออกโดย</span><span className="dv">{w.issued_by_name || '—'}</span></div>
            <div className="def">
              <span className="dk">การรับทราบ</span>
              <span className="dv"><AckTag w={w} /></span>
            </div>

            <div className="sec-label">เหตุการณ์</div>
            <div className="card">
              <div className="note" style={{ color: 'var(--t1)', fontSize: 14, lineHeight: 1.6 }}>
                {w.incident_detail || '—'}
              </div>
            </div>

            <Link to={`/warnings/${w.id}/print`}>
              <button className="cta ghost" style={{ marginTop: 18 }}>🖨 พิมพ์ใบเตือน / บันทึก PDF</button>
            </Link>

            {canAck && (
              <>
                {refuseMode && (
                  <div className="witness">
                    <label className="f">ชื่อพยาน (กรณีปฏิเสธการเซ็น)</label>
                    <input type="text" value={witness} onChange={(e) => setWitness(e.target.value)}
                      placeholder="เช่น หัวหน้ากะ / เพื่อนร่วมงาน" />
                  </div>
                )}
                <button className="cta" disabled={acking} onClick={() => doAck(refuseMode)}>
                  {acking ? 'กำลังบันทึก…' : refuseMode ? 'ยืนยันปฏิเสธ (มีพยาน)' : 'รับทราบใบเตือนนี้'}
                </button>
                <button className="cta ghost" style={{ marginTop: 10 }}
                  onClick={() => { setRefuseMode((v) => !v); setErr(''); }}>
                  {refuseMode ? 'ยกเลิก — กลับไปรับทราบปกติ' : 'ฉันขอปฏิเสธการรับทราบ'}
                </button>
              </>
            )}
          </>
        )}
      </section>
      <BottomNav />
    </div>
  );
}

function AckTag({ w }) {
  if (w.ack_status === 'acknowledged')
    return <span className="tag ok">รับทราบแล้ว{w.acknowledged_at ? ` · ${thaiDate(w.acknowledged_at)}` : ''}</span>;
  if (w.ack_status === 'refused_with_witness')
    return <span className="tag warn">ปฏิเสธ{w.witness_name ? ` · พยาน ${w.witness_name}` : ''}</span>;
  return <span className="tag pend">รอรับทราบ</span>;
}

const levelTh = (n) => ({ verbal: 'ตักเตือนวาจา', written: 'ลายลักษณ์อักษร', final: 'ครั้งสุดท้าย', terminate: 'พิจารณาเลิกจ้าง' }[n] || n || '');
